import AnimalInterface from './animalInterface';
import * as Constants from './animalConstants';
import OwnerClass from '../ownership/ownerClass';
import moment from 'moment';
import humanizeDuration from 'humanize-duration';

import {
    GiGoat,
    GiHorseHead,
    GiCow,
    GiSheep,
    GiSittingDog,
    GiPig
} from 'react-icons/gi';
import LandClass from '../land/landClass';

const animalIcons = {
    bovine: {
        bg: 'yellow.100',
        icon: GiCow
    },
    equine: {
        bg: 'red.100',
        icon: GiHorseHead
    },
    canine: {
        bg: 'blue.100',
        icon: GiSittingDog
    },
    porcine: {
        bg: 'purple.100',
        icon: GiPig
    },
    caprine: {
        bg: 'orange.100',
        icon: GiGoat
    },
    ovine: {
        bg: 'yellow.100',
        icon: GiSheep
    }
} as Record<string, any>;

export class AnimalCounter {
    amount: number;
    specie: string;
    type: string | undefined;
    gender: string | undefined;

    constructor(
        amount: number,
        data: { specie: string; type?: string; gender?: string | undefined }
    ) {
        this.amount = amount;
        this.specie = data.specie;
        this.type = data.type;
        this.gender = data.gender;
    }

    getIcon() {
        return animalIcons[this.specie || Constants.animalSpecies.bovine].icon;
    }

    add(amount: number) {
        this.amount += amount;
    }

    toJSON() {
        return {
            amount: this.amount,
            specie: this.specie,
            type: this.type,
            gender: this.gender
        };
    }
}
export default class AnimalClass extends OwnerClass implements AnimalInterface {
    land: string;
    landData: LandClass;
    mother: {
        id: string;
        name: string;
    };
    father: {
        id: string;
        name: string;
    };
    specie: 'bovine' | 'equine' | 'ovine' | 'porcine' | 'caprine' | 'canine';
    gender: 'male' | 'female' | undefined;
    type: 'adult' | 'child' | 'group';
    malesAmount: number;
    femalesAmount: number;
    state:
        | 'pregnancy'
        | 'breeding'
        | 'maintenance'
        | 'growing'
        | 'fattening'
        | 'ready'
        | 'sold';
    internalId: string;
    externalId: string;
    birthDate: string;

    age: string;

    shell: boolean;
    sellDate?: string;

    constructor(data: AnimalInterface) {
        super({ ...data });
        this.land = data.land;
        this.mother = data.mother;
        this.father = data.father;
        this.specie = data.specie;
        this.gender = data.gender;
        this.type = data.type;
        this.malesAmount = data.malesAmount;
        this.femalesAmount = data.femalesAmount;
        this.state = data.state;
        this.internalId = data.internalId;
        this.externalId = data.externalId;
        this.birthDate = data.birthDate;
        this.shell = data.shell;
        this.sellDate = data.sellDate;
    }

    is(specie: string) {
        return this.specie === specie;
    }

    setLand(lands: LandClass[]): AnimalClass {
        this.landData = lands.find(
            (land) => land.id === this.land
        ) as LandClass;
        return this;
    }

    getIcon() {
        return animalIcons[this.specie || Constants.animalSpecies.bovine].icon;
    }

    getDefaultImage(): string {
        const specie = this.specie || Constants.animalSpecies.bovine;
        const gender = this.gender || Constants.animalGenders.male;
        const type = this.type || Constants.animalTypes.adult;
        const name = `${specie}-${type}-${gender}`.toLowerCase();
        return `/images/animals/${name}.jpg`;
    }

    getBirthday() {
        return this.birthDate ? new Date(this.birthDate) : null;
    }

    getAge() {
        if (!this.age) {
            const birthDate = moment(this.birthDate);
            const today = moment(new Date());
            this.age = humanizeDuration(today.diff(birthDate), {
                language: navigator.language.substring(0, 2),
                round: true,
                units: ['y', 'mo', 'd']
            });
        }
        return this.age;
    }

    toJSON(): Record<string, any> {
        var json = super.toJSON();
        return {
            id: this.id,
            data: {
                ...json.data,
                land: this.land,
                mother: this.mother,
                father: this.father,
                specie: this.specie,
                gender: this.gender,
                malesAmount: this.malesAmount,
                femalesAmount: this.femalesAmount,
                type: this.type,
                state: this.state,
                internalId: this.internalId,
                externalId: this.externalId,
                birthDate: this.birthDate,
                shell: this.shell,
                sellDate: this.sellDate
            }
        };
    }
}

export class AnimalMother {
    mother: AnimalClass;
    childs: AnimalClass[];

    constructor(animal: AnimalClass, childs: AnimalClass[]) {
        this.mother = animal;
        this.childs = childs;
    }

    hasChild() {
        return this.childs?.length > 0;
    }

    hasChilds() {
        return this.childs?.length > 1;
    }

    getGroupedChilds() {
        const sibblings = this.childs;
        const grouped = (sibblings || [])
            .sort(
                (a, b) =>
                    new Date(a.birthDate).getTime() -
                    new Date(b.birthDate).getTime()
            )
            .reduce((acc, sibbling, i) => {
                const key = sibbling.birthDate;
                const animal = new AnimalClass(sibbling);
                let previous = 0;

                if (i) {
                    previous =
                        (new Date(sibblings[i].birthDate).getTime() -
                            new Date(sibblings[i - 1].birthDate).getTime()) /
                        86400000;
                }

                !acc[key]
                    ? (acc[key] = { previous, date: key, sibblings: [animal] })
                    : acc[key].sibblings.push(animal);

                return acc;
            }, {} as Record<string, any>);

        return grouped;
    }

    getFirstDelivery(humanized = true) {
        try {
            const birthDate = moment(this.mother.birthDate);
            const firstDeliveryDate = moment(
                Object.keys(this.getGroupedChilds())[0]
            );
            const firstDelivery = humanized
                ? humanizeDuration(firstDeliveryDate.diff(birthDate), {
                      language: navigator.language.substring(0, 2),
                      round: true,
                      units: ['y', 'mo', 'd']
                  })
                : firstDeliveryDate.diff(birthDate) / 86400000;
            return firstDelivery;
        } catch (e) {
            console.error('Error getting first delivery', e);
            return false;
        }
    }

    getAverageDelivery() {
        if (!this.childs || this.childs.length < 2) {
            return 0;
        }
        const grouped = this.getGroupedChilds();
        const childrensKeys = Object.keys(grouped);
        const first = grouped[childrensKeys[0]];
        const last = grouped[childrensKeys[childrensKeys.length - 1]];
        const average =
            (new Date(last.date).getTime() - new Date(first.date).getTime()) /
            (86400000 * (childrensKeys.length - 1));
        return Math.round(average);
    }
}
